import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { UserMessage } from "@bt-common/schemas/bt/user-message";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { useEffect } from "react";
import { useAppDispatch } from "@common-reducers/hooks/store.hook";
import { DBMessageLogThunks } from "@common-reducers/DBServiceThunks";
import { MessageLogCard } from "../../user-overview/tabs/message-logs/components/MessageLogCard";
import { isNotOnWhatsAppFailure, stripSendFailureInternals } from "@common-components/message-status/message-status-config";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";

/** Internal diagnostic keys the engine stamps on an error object — never shown to a user. */
const INTERNAL_ERROR_KEYS = ["userMessageId", "chatId", "secret"];

/** Render a failure error for display with all internal diagnostic ids/secrets stripped. */
function sanitizeFailureError(raw: unknown): string {
    if (typeof raw === "string") return stripSendFailureInternals(raw);
    if (raw && typeof raw === "object") {
        const clean = Object.fromEntries(
            Object.entries(raw as Record<string, unknown>).filter(([k]) => !INTERNAL_ERROR_KEYS.includes(k)),
        );
        return Object.keys(clean).length > 0 ? JSON.stringify(clean, null, 2) : "";
    }
    return "";
}

interface MessageDetailsSidebarProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    message: UserMessage | null;
}

export function MessageDetailsSidebar({ open, onOpenChange, message }: MessageDetailsSidebarProps) {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);

    useEffect(() => {
        if (open && message?._id) {
            // Fetch logs for this message to populate the store for MessageLogCard
            dispatch(DBMessageLogThunks.find({
                messageId: message._id
            }));
        }
    }, [open, message?._id, dispatch]);

    if (!message) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-7xl w-[90vw] max-h-[90vh] overflow-hidden flex flex-col p-0">
                <DialogHeader className="p-6 border-b">
                    <DialogTitle className="flex items-center gap-2">
                        Message Details
                        <Badge variant="outline">{message.status}</Badge>
                    </DialogTitle>
                    <DialogDescription>
                        ID: {message._id}
                    </DialogDescription>
                </DialogHeader>
                
                <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
                    <div className="px-6 pt-4">
                        <TabsList className="w-full justify-start">
                            <TabsTrigger value="details">Details</TabsTrigger>
                            <TabsTrigger value="logs">Logs & Debug</TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="details" className="flex-1 overflow-auto p-6 pt-2">
                        <ScrollArea className="h-full pr-4">
                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Due Date</h4>
                                        <p>{format(new Date(message.dueDate ?? 0), 'Pp')}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Created At</h4>
                                        <p>{message.createdAt ? format(new Date(message.createdAt), 'Pp') : '-'}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Owner</h4>
                                        <p className="font-mono text-sm">{(message as any).owner_populated?.email || String(message.owner)}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Contact</h4>
                                        <p>{message.contactName}</p>
                                        <p className="text-sm text-muted-foreground">{message.contactList?.[0]?.mobile}</p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Message Content</h4>
                                    <div className="bg-muted p-3 rounded-md text-sm whitespace-pre-wrap">
                                        {message.message}
                                    </div>
                                </div>

                                {message.sendFailureReason && (() => {
                                    const reasonText = isNotOnWhatsAppFailure(message)
                                        ? t('messageStatus.failedNotOnWhatsApp.description')
                                        : stripSendFailureInternals(String(message.sendFailureReason)) || String(message.sendFailureReason);
                                    const sanitizedError = sanitizeFailureError(message.sendFailureError);
                                    return (
                                        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-md border border-red-200 dark:border-red-800">
                                            <h4 className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Failure Reason</h4>
                                            <p className="text-sm text-red-700 dark:text-red-400">{reasonText}</p>
                                            {!!sanitizedError && (
                                                <pre className="mt-2 text-xs bg-black/5 dark:bg-black/40 p-2 rounded overflow-x-auto">
                                                    {sanitizedError}
                                                </pre>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </ScrollArea>
                    </TabsContent>

                    <TabsContent value="logs" className="flex-1 overflow-auto p-0">
                        <div className="px-6 py-4 space-y-6">
                             {/* Enhanced Message Log Card */}
                             <div className="pb-4">
                                <h3 className="font-semibold mb-4">Message Flow</h3>
                                <MessageLogCard messageId={message._id} message={message} />
                            </div>

                            <div>
                                <h3 className="font-semibold mb-2">Raw Data</h3>
                                <div className="bg-slate-950 p-4 rounded-md text-white">
                                    <pre className="text-xs overflow-auto max-h-[500px]">
                                        {JSON.stringify(message, null, 2)}
                                    </pre>
                                </div>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
}
